import { useState, useEffect } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import {
  DEMO_USER,
  DEMO_CERT_STATUSES,
  getProgramById,
  levelColor,
  type CertificationLevel,
} from "../lib/certifications";
import { fetchUserBadges, type BadgeRecord } from "../lib/sentinel-api";

interface BadgeDisplay {
  key: string;
  program_id: string;
  issued_at: string;
  verification_code: string;
}

export default function BadgesPage() {
  const [badges, setBadges] = useState<BadgeDisplay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserBadges(DEMO_USER.id).then((bdgs: BadgeRecord[]) => {
      if (bdgs.length) {
        setBadges(
          bdgs.map((b) => ({
            key: b.id,
            program_id: b.program_id,
            issued_at: b.issued_at,
            verification_code: b.verification_code,
          })),
        );
      } else {
        setBadges(
          DEMO_CERT_STATUSES.filter((c) => c.badge_issued).map((c) => ({
            key: c.program_id,
            program_id: c.program_id,
            issued_at: c.completion_date ?? "",
            verification_code: c.credential_code ?? "SNL-BADGE",
          })),
        );
      }
      setLoading(false);
    });
  }, []);

  return (
    <PlatformLayout>
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Digital Badges</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Your earned Sentinel digital badges. Share, verify, and download credentials issued through the E Industries certification engine.
        </p>
      </div>

      {loading ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <p className="text-muted" style={{ fontSize: "0.875rem" }}>Loading badges...</p>
        </div>
      ) : badges.length === 0 ? (
        <div className="card" style={{ padding: "var(--space-5)", textAlign: "center" }}>
          <div style={{ width: "64px", height: "64px", margin: "0 auto 0.5rem", borderRadius: "50%", background: "linear-gradient(135deg, var(--neutral-300), var(--neutral-400))", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/sentinel-white.png" alt="Sentinel" style={{ width: "36px", height: "36px", objectFit: "contain" }} />
          </div>
          <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}>
            No badges earned yet. Complete a certification program to earn your first digital badge.
          </p>
          <a href="/certifications" className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>
            Browse Certifications
          </a>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "var(--space-4)",
          }}
        >
          {badges.map((badge) => {
            const program = getProgramById(badge.program_id);
            const level: CertificationLevel = program?.level ?? "Professional";
            const title = program?.title ?? "Certification Badge";
            return (
              <div key={badge.key} className="card card-hover" style={{ padding: "var(--space-5)", textAlign: "center" }}>
                <div
                  style={{
                    width: "96px",
                    height: "96px",
                    margin: "0 auto 1rem",
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${levelColor(level)}, var(--primary-600))`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "var(--shadow-lg)",
                    overflow: "hidden",
                  }}
                >
                  <img src="/sentinel-white.png" alt="Sentinel Badge" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
                </div>
                <div style={{ fontWeight: 700, fontSize: "0.9375rem", marginBottom: "0.25rem" }}>{title}</div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>
                  {program?.industry ?? "Sentinel Certification"}
                </div>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginBottom: "0.5rem" }}>
                  Issued: {badge.issued_at || "N/A"}
                </div>
                <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)", marginBottom: "0.75rem", fontFamily: "monospace" }}>
                  {badge.verification_code}
                </div>
                <div style={{ display: "flex", gap: "0.375rem" }}>
                  <button className="btn btn-secondary" style={{ flex: 1, fontSize: "0.75rem", padding: "0.5rem 0.625rem" }}>
                    Share
                  </button>
                  <button className="btn btn-primary" style={{ flex: 1, fontSize: "0.75rem", padding: "0.5rem 0.625rem" }}>
                    Verify
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PlatformLayout>
  );
}
